import { apiRequest } from '../../api/client';

export type ChainStatus = 'Active' | 'Inactive';
export type BranchStatus = 'Active' | 'TemporarilyClosed' | 'Inactive';
export type TableClassName = 'A' | 'B' | 'C';

export interface Chain {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  /** Giới thiệu chuỗi (M14) — hiện khối "Giới thiệu" trên `s-brand`. Trống → khối ẩn. */
  description: string | null;
  /** Ảnh bìa `s-brand`. Trống → Member App rơi về gradient. */
  coverImageUrl: string | null;
  /** Link Zalo OA của thương hiệu. Trống → Member App ẩn hẳn nút Zalo. */
  zaloUrl: string | null;
  status: ChainStatus;
  branchCount: number;
}

export interface TableClass {
  class: TableClassName;
  tableCount: number;
  seedDiningMinutes: number;
}

export interface Branch {
  id: string;
  chainId: string;
  chainName: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  /** Dạng `HH:mm:ss` từ `TimeOnly` của backend */
  openTime: string;
  closeTime: string;
  phone: string | null;
  status: BranchStatus;
  tableClasses: TableClass[];
  /**
   * Bằng 0 nghĩa là **nhân viên không quét được hóa đơn tại chi nhánh này**
   * (BR-202). Prototype `a-branches` tô nền cảnh báo cho những dòng như vậy.
   */
  activeBankAccountCount: number;
}

/**
 * Hai thứ chi nhánh phải khai xong thì quầy mới chạy được (RL-214: STK ngân hàng nay **tùy chọn**,
 * không còn tính là bắt buộc):
 *
 * - thiếu ca làm → **không mở được ca**, nên bị chặn ở mọi thao tác quầy
 *   (policy `RequireOpenShift`), tức là sớm nhất
 * - thiếu mã cửa hàng POS → hóa đơn định dạng POS trả `INVALID_STORE_CODE`, không
 *   tích được điểm (BR-215 lớp (a), `S3-Web-02`)
 *
 * STK ngân hàng (`branch_bank_accounts`) vẫn cần để quét VietQR (BR-202), nhưng đó là điều kiện
 * lúc QUÉT chứ không phải điều kiện cấu hình chi nhánh — nên không còn tô cảnh báo hổ phách.
 */
export const branchBlockers = (shiftCount: number, storeCodeCount: number): string[] => {
  const blockers: string[] = [];
  if (shiftCount === 0) blockers.push('chưa khai ca làm');
  if (storeCodeCount === 0) blockers.push('chưa khai mã cửa hàng POS');
  return blockers;
};

export interface BranchShift {
  id: string;
  branchId: string;
  name: string;
  /** `HH:mm:ss` từ `TimeOnly` của backend */
  startTime: string;
  endTime: string;
  sortOrder: number;
  isActive: boolean;
  /**
   * Nhãn server ghép sẵn, ví dụ `Ca tối 17:00 tới 23:30`. **Không tự ghép ở client** —
   * đây đúng là chuỗi được chép vào `staff_shifts.shift_label` lúc nhân viên mở ca,
   * ghép hai nơi khác nhau là báo cáo lệch chữ.
   */
  displayLabel: string;
}

export interface BankAccount {
  id: string;
  branchId: string;
  bankBin: string;
  accountNo: string;
  accountName: string;
  isActive: boolean;
}

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

export const catalogApi = {
  listChains: () => unwrap(apiRequest<Chain[]>('/admin/catalog/chains')),

  createChain: (body: {
    name: string;
    slug: string;
    logoUrl?: string | null;
    description?: string | null;
    coverImageUrl?: string | null;
    zaloUrl?: string | null;
  }) => unwrap(apiRequest<Chain>('/admin/catalog/chains', { method: 'POST', body })),

  updateChain: (
    id: string,
    body: {
      name: string;
      logoUrl?: string | null;
      description?: string | null;
      coverImageUrl?: string | null;
      zaloUrl?: string | null;
      status: ChainStatus;
    },
  ) => unwrap(apiRequest<Chain>(`/admin/catalog/chains/${id}`, { method: 'PUT', body })),

  listBranches: () => unwrap(apiRequest<Branch[]>('/admin/catalog/branches')),

  createBranch: (body: BranchInput & { chainId: string; tableClasses?: TableClass[] }) =>
    unwrap(apiRequest<Branch>('/admin/catalog/branches', { method: 'POST', body })),

  updateBranch: (id: string, body: BranchInput & { status: BranchStatus }) =>
    unwrap(apiRequest<Branch>(`/admin/catalog/branches/${id}`, { method: 'PUT', body })),

  setTableClasses: (branchId: string, classes: TableClass[]) =>
    unwrap(
      apiRequest<TableClass[]>(`/admin/catalog/branches/${branchId}/table-classes`, {
        method: 'PUT',
        body: { classes },
      }),
    ),

  listBranchShifts: (branchId: string) =>
    unwrap(apiRequest<BranchShift[]>(`/admin/catalog/branches/${branchId}/shifts`)),

  createBranchShift: (
    branchId: string,
    body: { name: string; startTime: string; endTime: string; sortOrder: number },
  ) =>
    unwrap(
      apiRequest<BranchShift>(`/admin/catalog/branches/${branchId}/shifts`, {
        method: 'POST',
        body,
      }),
    ),

  updateBranchShift: (
    id: string,
    body: {
      name: string;
      startTime: string;
      endTime: string;
      sortOrder: number;
      isActive: boolean;
    },
  ) => unwrap(apiRequest<BranchShift>(`/admin/catalog/shifts/${id}`, { method: 'PUT', body })),

  listBankAccounts: (branchId: string) =>
    unwrap(apiRequest<BankAccount[]>(`/admin/catalog/branches/${branchId}/bank-accounts`)),

  addBankAccount: (branchId: string, body: { bankBin: string; accountNo: string; accountName: string }) =>
    unwrap(
      apiRequest<BankAccount>(`/admin/catalog/branches/${branchId}/bank-accounts`, {
        method: 'POST',
        body,
      }),
    ),

  listPosStoreCodes: () => unwrap(apiRequest<PosStoreCode[]>('/admin/catalog/pos-store-codes')),

  addPosStoreCode: (body: { branchId: string; storeCode: string; label?: string | null }) =>
    unwrap(
      apiRequest<PosStoreCode>('/admin/catalog/pos-store-codes', {
        method: 'POST',
        body,
      }),
    ),

  setPosStoreCodeActive: (id: string, isActive: boolean) =>
    unwrap(
      apiRequest<PosStoreCode>(`/admin/catalog/pos-store-codes/${id}/active`, {
        method: 'PUT',
        body: { isActive },
      }),
    ),

  setBankAccountActive: (id: string, isActive: boolean) =>
    unwrap(
      apiRequest<BankAccount>(`/admin/catalog/bank-accounts/${id}/active`, {
        method: 'PUT',
        body: { isActive },
      }),
    ),
};

export interface BranchInput {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  openTime: string;
  closeTime: string;
  phone?: string | null;
}

/** Khóa cache TanStack Query — gom một chỗ để không gõ lệch chuỗi giữa các file. */
/**
 * Mã cửa hàng POS in trên hóa đơn (ADR-008) — danh sách trắng của lớp (a) BR-215.
 *
 * Không khai thì mọi hóa đơn định dạng POS trả `INVALID_STORE_CODE`, nên đây là bước bắt buộc
 * trước khi mở tích điểm ở một chi nhánh.
 */
export interface PosStoreCode {
  id: string;
  branchId: string;
  branchName: string;
  storeCode: string;
  label?: string | null;
  isActive: boolean;
}

export const catalogKeys = {
  chains: ['catalog', 'chains'] as const,
  branches: ['catalog', 'branches'] as const,
  bankAccounts: (branchId: string) => ['catalog', 'bank-accounts', branchId] as const,
  posStoreCodes: ['catalog', 'pos-store-codes'] as const,
  branchShifts: (branchId: string) => ['catalog', 'branch-shifts', branchId] as const,
  /** Ca của MỌI chi nhánh — bảng chi nhánh cần đếm ca để biết dòng nào còn thiếu */
  allBranchShifts: ['catalog', 'branch-shifts'] as const,
};
